// OWNER: FRONTEND-ADMIN. Settings → Users (SPEC §15.9): create, role, active, reset password, certify-exempt,
// campaign assignments. CORE contract: GET/POST /api/users, PATCH /api/users/:id, PUT /api/users/:id/campaigns.
import { useState } from 'preact/hooks';
import type { Campaign, Role } from '../../../src/shared/api';
import type { UserAdmin } from '../../../src/shared/api-core';
import { errorMessage } from '../api';
import { ErrorBanner, Modal, fmtDateTime, toast } from '../components/ui';
import { useMe } from '../me';
import { adminApi } from './endpoints';
import { Check, Field, inputValue, useLoad } from './ui';

const MIN_PASSWORD = 10; // CORE: >= 10 characters

/** 16 characters from an unambiguous alphabet, using the browser CSPRNG. */
export function generatePassword(len = 16): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789-_!';
  const limit = 256 - (256 % alphabet.length); // rejection sampling: no modulo bias
  let out = '';
  while (out.length < len) {
    const bytes = new Uint8Array(len * 2);
    crypto.getRandomValues(bytes);
    for (const b of bytes) if (b < limit && out.length < len) out += alphabet[b % alphabet.length];
  }
  return out;
}

const emailOk = (s: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());

type Dialog = { kind: 'create' } | { kind: 'edit'; u: UserAdmin } | { kind: 'password'; u: UserAdmin } | { kind: 'campaigns'; u: UserAdmin };

export function UsersAdmin() {
  const me = useMe();
  const users = useLoad(() => adminApi.users(), []);
  const campaigns = useLoad(() => adminApi.campaigns(), []);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const replace = (u: UserAdmin) => {
    const list = users.data ?? [];
    users.setData(list.some((x) => x.id === u.id) ? list.map((x) => (x.id === u.id ? u : x)) : [...list, u]);
  };
  const campaignNames = (ids: number[]) => ids.map((id) => campaigns.data?.find((c) => c.id === id)?.name ?? `#${id}`);
  const rows = (users.data ?? []).filter((u) => showInactive || u.active).sort((a, b) => a.role.localeCompare(b.role) || a.name.localeCompare(b.name));

  return (
    <div class="stack">
      <div class="adm-toolbar">
        <Check checked={showInactive} onChange={setShowInactive} label="Show deactivated users" />
        <span class="grow" />
        {/* SPEC §19: new teammates are invited by email above (they set their own password); no admin-set passwords. */}
      </div>
      <ErrorBanner error={users.error} />
      <ErrorBanner error={campaigns.error} />
      {rows.length ? (
        <div class="adm-table-wrap">
          <table>
            <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Status</th><th>Certification</th><th>Campaigns</th>{me.authMode === 'password' ? <th>Password</th> : null}<th>Added</th><th /></tr></thead>
            <tbody>{rows.map((u) => (
              <tr class={u.active ? '' : 'adm-dim'}>
                <td><b>{u.name}</b>{u.id === me.id ? <span class="muted"> (you)</span> : null}
                  {u.setterLevel === 'growth_specialist' ? <div><span class="chip chip-ok" title="Approves their own voicemail drops">Growth Specialist</span></div> : null}</td>
                <td>{u.email}{u.emailFrom !== undefined ? <div class="muted" style="font-size:12px">sends as {u.emailFrom ?? 'hello@valistix.com (fallback)'}</div> : null}
                  {u.jobTitle !== undefined ? <div class="muted" style="font-size:12px">signature: {u.jobTitle ? `${u.jobTitle} · Valistix Inc.` : 'first name only (no job title)'}</div> : null}</td>
                <td>{u.role}</td>
                <td>{u.active ? <span class="chip chip-ok">active</span> : <span class="chip">deactivated</span>}</td>
                <td>{u.certifyExempt ? <span class="chip chip-warn">exempt</span> : <span class="muted">practice required</span>}</td>
                <td style="max-width:280px">{u.campaignIds.length ? campaignNames(u.campaignIds).join(', ') : <span class="muted">none</span>}</td>
                {me.authMode === 'password' ? <td>{u.hasPassword ? 'set' : <span class="chip chip-warn">not set</span>}</td> : null}
                <td>{fmtDateTime(u.createdAt)}</td>
                <td><div class="row" style="gap:4px;flex-wrap:wrap">
                  <button class="btn btn-sm" onClick={() => setDialog({ kind: 'edit', u })}>Edit</button>
                  <button class="btn btn-sm" onClick={() => setDialog({ kind: 'campaigns', u })}>Campaigns</button>
                  {me.authMode === 'password' ? <button class="btn btn-sm" onClick={() => setDialog({ kind: 'password', u })}>Reset password</button> : null}
                </div></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      ) : users.data ? <div class="muted">No users.</div> : null}
      {me.authMode === 'access' ? <div class="adm-sub">Sign-in is through Cloudflare Access: a user must also be allowed in the Access policy, and their Access email must match here.</div> : null}

      {dialog?.kind === 'create' ? <CreateUserModal campaigns={campaigns.data ?? []} onDone={replace} onClose={() => setDialog(null)} /> : null}
      {dialog?.kind === 'edit' ? <EditUserModal user={dialog.u} onDone={replace} onClose={() => setDialog(null)} /> : null}
      {dialog?.kind === 'password' ? <PasswordModal user={dialog.u} onDone={replace} onClose={() => setDialog(null)} /> : null}
      {dialog?.kind === 'campaigns' ? <UserCampaignsModal user={dialog.u} campaigns={campaigns.data ?? []} onDone={replace} onClose={() => setDialog(null)} /> : null}
    </div>
  );
}

function PasswordField(props: { value: string; onChange: (v: string) => void; required: boolean }) {
  const [shown, setShown] = useState(false);
  const short = props.value.length > 0 && props.value.length < MIN_PASSWORD;
  return (
    <Field label={`Password${props.required ? '' : ' (optional)'}`} hint={short ? `At least ${MIN_PASSWORD} characters.` : 'Give it to them privately (not by email with their username). They can’t change it themselves yet.'}>
      <div class="row">
        <input type={shown ? 'text' : 'password'} value={props.value} autocomplete="new-password" style="flex:1" onInput={(e) => props.onChange(inputValue(e))} />
        <button type="button" class="btn btn-sm" onClick={() => { props.onChange(generatePassword()); setShown(true); }}>Generate</button>
        <button type="button" class="btn btn-sm" disabled={!props.value} onClick={() => {
          navigator.clipboard?.writeText(props.value).then(() => toast('Password copied.', 'ok'), () => toast('Couldn’t copy; select it and copy by hand.', 'warn'));
        }}>Copy</button>
        <button type="button" class="btn btn-sm" onClick={() => setShown(!shown)}>{shown ? 'Hide' : 'Show'}</button>
      </div>
    </Field>
  );
}

function CreateUserModal(props: { campaigns: Campaign[]; onDone: (u: UserAdmin) => void; onClose: () => void }) {
  const me = useMe();
  const needPassword = me.authMode === 'password';
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('setter');
  const [password, setPassword] = useState('');
  const [certifyExempt, setCertifyExempt] = useState(false);
  const [campaignIds, setCampaignIds] = useState<number[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const problems: string[] = [];
  if (!name.trim()) problems.push('Enter their name.');
  if (!emailOk(email)) problems.push('Enter a valid email.');
  if (needPassword && password.length < MIN_PASSWORD) problems.push(`Set a password of at least ${MIN_PASSWORD} characters.`);
  if (!needPassword && password && password.length < MIN_PASSWORD) problems.push(`Passwords need at least ${MIN_PASSWORD} characters.`);

  const save = async () => {
    if (problems.length) { setError(problems[0]); return; }
    setBusy(true); setError(null);
    try {
      const u = await adminApi.createUser({ name: name.trim(), email: email.trim().toLowerCase(), role, certifyExempt, campaignIds, ...(password ? { password } : {}) });
      toast(`Added ${u.name}.`, 'ok');
      props.onDone(u);
      props.onClose();
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  };

  return (
    <Modal title="Add user" onClose={props.onClose} width={600}>
      <div class="stack">
        <div class="adm-grid">
          <Field label="Name"><input value={name} onInput={(e) => setName(inputValue(e))} /></Field>
          <Field label="Email"><input type="email" value={email} onInput={(e) => setEmail(inputValue(e))} /></Field>
          <Field label="Role">
            <select value={role} onChange={(e) => setRole(inputValue(e) as Role)}><option value="setter">Setter</option><option value="admin">Admin</option></select>
          </Field>
        </div>
        {me.authMode !== 'access' ? <PasswordField value={password} onChange={setPassword} required={needPassword} /> : null}
        <Check checked={certifyExempt} onChange={setCertifyExempt} label="Certify-exempt: may dial without passing practice certification (experienced closers only)" />
        {props.campaigns.length ? (
          <div class="stack" style="gap:4px">
            <span class="eyebrow">Campaigns</span>
            {props.campaigns.filter((c) => c.status !== 'done').map((c) => (
              <Check checked={campaignIds.includes(c.id)} onChange={(on) => setCampaignIds((cur) => (on ? [...cur.filter((x) => x !== c.id), c.id] : cur.filter((x) => x !== c.id)))} label={<span>{c.name} <span class="muted">· {c.status}</span></span>} />
            ))}
          </div>
        ) : null}
        <ErrorBanner error={error} />
        <div class="row" style="justify-content:flex-end">
          <button class="btn" onClick={props.onClose}>Cancel</button>
          <button class="btn btn-primary" disabled={busy || problems.length > 0} title={problems[0]} onClick={save}>{busy ? 'Adding…' : 'Add user'}</button>
        </div>
      </div>
    </Modal>
  );
}

function EditUserModal(props: { user: UserAdmin; onDone: (u: UserAdmin) => void; onClose: () => void }) {
  const me = useMe();
  const u = props.user;
  const self = u.id === me.id;
  const [name, setName] = useState(u.name);
  const [role, setRole] = useState<Role>(u.role);
  const [active, setActive] = useState(u.active);
  const [certifyExempt, setCertifyExempt] = useState(u.certifyExempt);
  const [emailFrom, setEmailFrom] = useState(u.emailFrom ?? '');
  const [jobTitle, setJobTitle] = useState(u.jobTitle ?? '');
  const [level, setLevel] = useState<'setter' | 'growth_specialist'>(u.setterLevel ?? 'setter');
  const levelSupported = u.setterLevel !== undefined;   // undefined: the server has no migration 0023 yet
  const titleSupported = u.jobTitle !== undefined;   // undefined: the server has no migration 0022 yet
  const titleClean = jobTitle.replace(/\s+/g, ' ').trim();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fromSupported = u.emailFrom !== undefined;   // undefined: the server has no migration 0020 yet
  const fromClean = emailFrom.trim().toLowerCase();
  const save = async () => {
    if (!name.trim()) { setError('The name can’t be empty.'); return; }
    if (fromSupported && fromClean && !/^[a-z0-9._%+-]+@valistix\.com$/.test(fromClean)) { setError('The sender address must be an @valistix.com address.'); return; }
    setBusy(true); setError(null);
    try {
      const next = await adminApi.updateUser(u.id, {
        ...(name.trim() !== u.name ? { name: name.trim() } : {}),
        ...(role !== u.role ? { role } : {}),
        ...(active !== u.active ? { active } : {}),
        ...(certifyExempt !== u.certifyExempt ? { certifyExempt } : {}),
        ...(fromSupported && fromClean !== (u.emailFrom ?? '') ? { emailFrom: fromClean || null } : {}),
        ...(titleSupported && titleClean !== (u.jobTitle ?? '') ? { jobTitle: titleClean || null } : {}),
        ...(levelSupported && level !== (u.setterLevel ?? 'setter') ? { setterLevel: level } : {}),
      });
      toast(`Saved ${next.name}.`, 'ok');
      props.onDone(next);
      props.onClose();
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  };
  return (
    <Modal title={`Edit ${u.name}`} onClose={props.onClose}>
      <div class="stack">
        <div class="adm-sub">{u.email} · Twilio identity <span class="mono">{u.twilioIdentity}</span></div>
        <Field label="Name"><input value={name} onInput={(e) => setName(inputValue(e))} /></Field>
        <Field label="Role" hint={self ? 'You can’t change your own role (so the team never loses its last admin by accident).' : undefined}>
          <select value={role} disabled={self} onChange={(e) => setRole(inputValue(e) as Role)}><option value="setter">Setter</option><option value="admin">Admin</option></select>
        </Field>
        <Check checked={active} disabled={self} onChange={setActive} label={self ? 'Active (you can’t deactivate yourself)' : 'Active (deactivating signs them out and stops all dialling)'} />
        <Check checked={certifyExempt} onChange={setCertifyExempt} label="Certify-exempt: may dial without passing practice certification" />
        {fromSupported ? (
          <Field label="Sends follow-up emails as" hint="Their own @valistix.com address: From “First name from Valistix”, replies come back to it, and a BCC copy lands there. Empty = hello@valistix.com.">
            <input type="email" value={emailFrom} placeholder="name@valistix.com" onInput={(e) => setEmailFrom(inputValue(e))} />
          </Field>
        ) : null}
        {titleSupported ? (
          <Field label="Job title (email signature)" hint="Shown in their branded signature as “Title · Valistix Inc.”, e.g. Growth Consultant. Empty = the email is signed with their first name only.">
            <input value={jobTitle} maxLength={60} placeholder="Growth Consultant" onInput={(e) => setJobTitle(inputValue(e))} />
          </Field>
        ) : null}
        {levelSupported && role === 'setter' ? (
          <Field label="Level" hint="A Growth Specialist approves their own voicemail drops when they upload them (they tick the same legal checklist you would). Setters' recordings wait for your approval.">
            <select value={level} onChange={(e) => setLevel(inputValue(e) as 'setter' | 'growth_specialist')}>
              <option value="setter">Setter</option>
              <option value="growth_specialist">Growth Specialist</option>
            </select>
          </Field>
        ) : null}
        {certifyExempt && !u.certifyExempt ? <div class="adm-muted-box">Exempt users skip the practice-call certification for every campaign. Use it only for people you have heard on the phone.</div> : null}
        <ErrorBanner error={error} />
        <div class="row" style="justify-content:flex-end">
          <button class="btn" onClick={props.onClose}>Cancel</button>
          <button class="btn btn-primary" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </Modal>
  );
}

function PasswordModal(props: { user: UserAdmin; onDone: (u: UserAdmin) => void; onClose: () => void }) {
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    if (password.length < MIN_PASSWORD) { setError(`At least ${MIN_PASSWORD} characters.`); return; }
    setBusy(true); setError(null);
    try {
      const next = await adminApi.updateUser(props.user.id, { password });
      toast(`Password reset for ${next.name}. They were signed out everywhere.`, 'ok');
      props.onDone(next);
      props.onClose();
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  };
  return (
    <Modal title={`Reset password · ${props.user.name}`} onClose={props.onClose}>
      <div class="stack">
        <div class="adm-sub">This signs {props.user.name} out on every device. Copy the new password before saving.</div>
        <PasswordField value={password} onChange={setPassword} required />
        <ErrorBanner error={error} />
        <div class="row" style="justify-content:flex-end">
          <button class="btn" onClick={props.onClose}>Cancel</button>
          <button class="btn btn-danger" disabled={busy || password.length < MIN_PASSWORD} onClick={save}>{busy ? 'Saving…' : 'Reset password'}</button>
        </div>
      </div>
    </Modal>
  );
}

function UserCampaignsModal(props: { user: UserAdmin; campaigns: Campaign[]; onDone: (u: UserAdmin) => void; onClose: () => void }) {
  const [ids, setIds] = useState<number[]>(props.user.campaignIds);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const list = props.campaigns.filter((c) => c.status !== 'done' || ids.includes(c.id));
  const save = async () => {
    setBusy(true); setError(null);
    try {
      const next = await adminApi.setUserCampaigns(props.user.id, ids);
      toast(`Campaigns saved for ${next.name}.`, 'ok');
      props.onDone(next);
      props.onClose();
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  };
  return (
    <Modal title={`Campaigns · ${props.user.name}`} onClose={props.onClose}>
      <div class="stack">
        <div class="adm-sub">They can only see and dial campaigns ticked here, and must be certified on each (unless exempt).</div>
        {list.length === 0 ? <div class="muted">No campaigns yet.</div> : null}
        {list.map((c) => (
          <Check checked={ids.includes(c.id)} onChange={(on) => setIds((cur) => (on ? [...cur.filter((x) => x !== c.id), c.id] : cur.filter((x) => x !== c.id)))}
            label={<span>{c.name} <span class="muted">· {c.status}{c.weekStart ? ` · wk ${c.weekStart}` : ''}</span></span>} />
        ))}
        <ErrorBanner error={error} />
        <div class="row" style="justify-content:flex-end">
          <button class="btn" onClick={props.onClose}>Cancel</button>
          <button class="btn btn-primary" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </Modal>
  );
}
